import React from "react";
import LegalLayout, { LegalH2, LegalP, LegalUL } from "./LegalLayout";

export default function RefundPolicy() {
  return (
    <LegalLayout
      testId="refund-policy-page"
      overline="Rider First"
      title="Refunds & Cancellation Policy"
      updated="February 2026"
    >
      <LegalP>
        We take pride in every piece of gear we ship. If something isn't right, we'll do our best to
        make it right. This policy explains when and how you can cancel an order, return a product,
        and receive a refund.
      </LegalP>

      <LegalH2>1. Order Cancellation</LegalH2>
      <LegalUL>
        <li>
          <strong>Before dispatch:</strong> You may cancel your order free of charge any time before
          it has been dispatched from our warehouse. Cancellations can be requested by emailing
          {" "}<a href="mailto:hello@mileforge.in" className="text-[#D96A1B] hover:underline">hello@mileforge.in</a>
          {" "}with your order number.
        </li>
        <li>
          <strong>After dispatch:</strong> Once the order has been handed over to our courier partner,
          it cannot be cancelled. You may however refuse delivery (see Returns below).
        </li>
        <li>
          MileForge reserves the right to cancel an order at its discretion in cases of stock
          unavailability, pricing errors, suspected fraudulent activity, or if the delivery address
          is outside our serviceable pincodes. In such cases a full refund will be processed.
        </li>
      </LegalUL>

      <LegalH2>2. Returns & Exchanges</LegalH2>
      <LegalP>
        We accept returns and exchanges within <strong>7 days</strong> of delivery, subject to the
        following conditions:
      </LegalP>
      <LegalUL>
        <li>The product must be unused, unwashed, and in its original condition with all tags, labels, and packaging intact.</li>
        <li>Items with visible wear, tear, damage caused by the customer, or missing accessories are not eligible.</li>
        <li>Innerwear, base layers, and clearance/sale items are non-returnable for hygiene and inventory reasons.</li>
        <li>Return requests must be raised within 7 days of delivery via email at <a href="mailto:hello@mileforge.in" className="text-[#D96A1B] hover:underline">hello@mileforge.in</a> along with photographs of the product and the invoice.</li>
      </LegalUL>

      <LegalH2>3. Damaged, Defective or Wrong Items</LegalH2>
      <LegalP>
        If you receive a damaged, defective, or incorrect product, please write to us within
        <strong> 48 hours of delivery</strong> with clear photos of:
      </LegalP>
      <LegalUL>
        <li>The item(s) received</li>
        <li>The outer packaging</li>
        <li>The invoice / shipping label</li>
      </LegalUL>
      <LegalP>
        We will arrange a free reverse pickup and either replace the item at no extra cost or issue
        a full refund — your choice.
      </LegalP>

      <LegalH2>4. Reverse Pickup</LegalH2>
      <LegalUL>
        <li>If your pincode is serviceable by our courier partners, we will arrange a free reverse pickup for defective/wrong items.</li>
        <li>For change-of-mind returns, a flat return shipping fee of ₹99 may be deducted from your refund unless waived at our discretion.</li>
        <li>If your pincode is non-serviceable for reverse pickup, you can courier the item back to us and we will reimburse the standard courier fee up to ₹150 on presentation of a receipt.</li>
      </LegalUL>

      <LegalH2>5. Refund Timeline & Method</LegalH2>
      <LegalUL>
        <li>Once we receive and inspect the returned item, a refund will be initiated within <strong>3 business days</strong>.</li>
        <li>Refunds are processed to the original payment source (UPI / card / netbanking) via Cashfree.</li>
        <li>Depending on your bank, funds typically reflect in <strong>5-7 business days</strong> after initiation.</li>
        <li>For Cash on Delivery (COD) orders, refunds are issued via UPI/bank transfer — we will collect your details at the time of the return request.</li>
        <li>An email confirmation is sent when your refund is initiated and again once it is completed.</li>
      </LegalUL>

      <LegalH2>6. Size Exchanges</LegalH2>
      <LegalP>
        We offer a one-time size exchange (if available in stock) within 7 days of delivery. Please
        refer to the size chart on each product page before ordering to minimise the need for exchanges.
      </LegalP>

      <LegalH2>7. Non-Refundable Situations</LegalH2>
      <LegalUL>
        <li>Refunds are not issued for delays caused by courier partners, natural disasters, strikes, or events outside our reasonable control.</li>
        <li>Products purchased on final-sale/clearance are not eligible for return, exchange, or refund unless received damaged.</li>
        <li>Requests raised after the 7-day return window will not be entertained.</li>
      </LegalUL>

      <LegalH2>8. How to Raise a Return / Refund Request</LegalH2>
      <LegalP>
        Email <a href="mailto:hello@mileforge.in" className="text-[#D96A1B] hover:underline">hello@mileforge.in</a>
        {" "}with the subject line "Return Request — [Order Number]" and include:
      </LegalP>
      <LegalUL>
        <li>Your order number</li>
        <li>Reason for the return</li>
        <li>Clear photographs of the product</li>
        <li>Preferred resolution — refund, replacement, or size exchange</li>
      </LegalUL>
      <LegalP>
        Our support team responds within one business day. You can also WhatsApp us at
        {" "}<a href="https://wa.me/919884041979" target="_blank" rel="noreferrer" className="text-[#D96A1B] hover:underline">+91 98840 41979</a>
        {" "}for a faster response.
      </LegalP>
    </LegalLayout>
  );
}
